/**
 * QuickItemChips.jsx — 발주량 상위 홍보물 빠른 추가 칩 패널
 *
 * 모달 바깥 오른쪽에 렌더링. 모달 본문을 맨 아래까지 스크롤하면
 * spring 애니메이션으로 나타나며, 클릭 즉시 홍보물이 추가됨.
 *
 * props:
 *  - items        : 활성 홍보물 전체 목록 (total_order_qty 포함)
 *  - disabledIds  : 이미 추가된 item_id 배열 → ✓ 비활성 처리
 *  - onSelect(item): 칩 클릭 콜백
 *
 * 확장성:
 *  - CHIPS_COUNT 상수로 노출 개수 조절
 *  - 추후 "빠른선택 통계" 테이블 연동 시 onSelect 내부에서 logging API 호출 가능
 */
import React, { useEffect } from 'react';

/** 기본 노출 칩 개수 */
const CHIPS_COUNT = 15;

/** CSS spring 애니메이션 — DOM에 한 번만 주입 */
const STYLE_ID = 'quick-item-chips-anim';
function injectAnimation() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes qicPanelIn {
      0%   { opacity: 0; transform: translateX(32px) scale(0.96); }
      55%  { transform: translateX(-5px) scale(1.02); }
      78%  { transform: translateX(3px) scale(0.99); }
      100% { opacity: 1; transform: translateX(0) scale(1); }
    }
    @keyframes qicChipIn {
      0%   { opacity: 0; transform: translateY(10px) scale(0.88); }
      65%  { transform: translateY(-2px) scale(1.04); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    .qic-chip:not(:disabled):hover {
      background: rgba(167,139,250,0.18) !important;
      border-color: rgba(124,58,237,0.7) !important;
      transform: translateY(-1px);
    }
    .qic-chip:not(:disabled):active {
      transform: scale(0.94) !important;
    }
  `;
  document.head.appendChild(el);
}

export default function QuickItemChips({ items = [], disabledIds = [], onSelect }) {
  // 애니메이션 CSS 주입 (마운트 시 1회)
  useEffect(() => { injectAnimation(); }, []);

  // 발주량 내림차순 정렬 → 상위 CHIPS_COUNT개
  const top = [...items]
    .sort((a, b) => (b.total_order_qty || 0) - (a.total_order_qty || 0))
    .slice(0, CHIPS_COUNT);

  const disabledSet = new Set(disabledIds.filter(Boolean));

  return (
    <div
      style={{
        width: 158,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        // 패널 전체 spring 슬라이드 인
        animation: 'qicPanelIn 0.42s cubic-bezier(0.34,1.56,0.64,1) both',
      }}
    >
      {/* 패널 헤더 */}
      <div style={{
        fontSize: 10, fontWeight: 700,
        color: 'rgba(255,255,255,0.60)',
        letterSpacing: '0.07em', textTransform: 'uppercase',
        paddingLeft: 2, marginBottom: 3,
      }}>
        빠른 추가 ↓발주량
      </div>

      {/* 칩 목록 */}
      {top.map((item, idx) => {
        const disabled = disabledSet.has(item.id);
        return (
          <button
            key={item.id}
            className="qic-chip"
            disabled={disabled}
            onClick={() => onSelect(item)}
            title={`${item.name}${item.total_order_qty > 0 ? ` (발주 ${item.total_order_qty}건)` : ''}`}
            style={{
              // 칩별 순차 등장 (stagger)
              animation: `qicChipIn 0.28s cubic-bezier(0.34,1.56,0.64,1) ${idx * 0.032}s both`,
              width: '100%',
              textAlign: 'left',
              padding: '6px 10px',
              border: disabled
                ? '1px solid rgba(255,255,255,0.10)'
                : '1px solid rgba(167,139,250,0.50)',
              borderRadius: 8,
              fontSize: 11,
              fontWeight: disabled ? 400 : 600,
              background: disabled
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(255,255,255,0.92)',
              color: disabled ? 'rgba(255,255,255,0.25)' : 'var(--text-sub)',
              cursor: disabled ? 'default' : 'pointer',
              transition: 'background 0.12s, border-color 0.12s, transform 0.1s',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
            }}
          >
            {disabled
              ? <><span style={{ marginRight: 5, fontSize: 10 }}>✓</span>{item.name}</>
              : item.name
            }
          </button>
        );
      })}
    </div>
  );
}
